import { useEffect, useMemo, useRef, useState } from 'react';
import { TRACKS } from '../constants/appConstants.js';
import {
  getLearnerState, getLesson, getFirstLesson, getNextLesson,
  markLessonComplete, upsertLessonProgress, upsertLearnerState,
  logYoutubeInteraction, buildStatePayload,
} from '../services/supabaseClient.js';
import { callAI } from '../services/aiService.js';
import { parseResponse } from '../utils/parser.js';
import { isEntertainmentHit, rerankCandidates } from '../utils/youtubeFilters.js';
import QuizPanel from './QuizPanel.jsx';

// ─────────────────────────────────────────────────────────────────
// VideoLessonTool — Dashboard → Study Tools → Video Lesson.
//
// Picks Current vs Next lesson (capped at 2 options), surfaces a short
// (≤10 min) YouTube clip for it, and lets the learner take a real
// CHECKPOINT_READY quiz via the chat API. Pass (≥80%) marks the lesson
// complete and advances current_lesson_id; fail persists the failed-
// checkpoint state and hands off to the Study Console so the engine's
// normal remediation flow takes over from there. Watching the video is
// a preview — never a completion step.
//
// Engine is NOT instantiated here. handleQuizComplete in useLearningEngine
// reads private refs and starts a chat session; replicating its passing
// /failing persistence path via supabaseClient avoids cross-session state
// collisions with an open Study Console tab and respects the "don't modify
// useLearningEngine" constraint.
// ─────────────────────────────────────────────────────────────────

const PASS_THRESHOLD = 0.80;
const YT_DURATION_CAP_SEC = 600; // 10 minutes per spec

function parseIsoDuration(iso) {
  const m = (iso || '').match(/PT(?:(\d+)M)?(?:(\d+)S)?/);
  return (parseInt(m?.[1] || '0', 10) * 60) + parseInt(m?.[2] || '0', 10);
}

function fmtDur(sec) {
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

export default function VideoLessonTool({ learner, onSelectTrack }) {
  const trackId = learner?.current_track || null;
  const track   = useMemo(() => (trackId ? TRACKS[trackId] : null), [trackId]);

  const [bootError,     setBootError]     = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [nextLesson,    setNextLesson]    = useState(null);
  const [selectedKey,   setSelectedKey]   = useState('current'); // 'current' | 'next'

  const [video,         setVideo]         = useState(null);   // { id, title, duration } | null
  const [videoLoading,  setVideoLoading]  = useState(false);
  const [videoMissing,  setVideoMissing]  = useState(false);
  // Per-lesson cache so toggling Current ↔ Next doesn't re-hit YouTube.
  const videoCacheRef = useRef(new Map()); // lessonId -> { video, missing }

  const [phase,         setPhase]         = useState('select'); // select | quiz | passed | failed
  const [quiz,          setQuiz]          = useState(null);
  const [quizLoading,   setQuizLoading]   = useState(false);
  const [quizError,     setQuizError]     = useState(null);

  // ── Load current + next lesson once per learner/track ────────────
  useEffect(() => {
    if (!learner?.id) { setBootError('Sign in to use Video Lesson.'); return; }
    if (!trackId)     { setBootError('Pick a track on the Dashboard first.'); return; }

    let cancelled = false;
    (async () => {
      try {
        const state = await getLearnerState(learner.id, trackId);
        // Bleed-guard: stored current_lesson_id is only accepted if it really
        // belongs to the active track (matches ContinueLearningCard's logic).
        let cur = null;
        if (state?.current_lesson_id) {
          const candidate = await getLesson(state.current_lesson_id);
          if (candidate?.modules?.track_id === trackId) cur = candidate;
        }
        if (!cur) cur = await getFirstLesson(trackId);
        if (!cur) { if (!cancelled) setBootError('No lessons available on this track yet.'); return; }

        let nxt = null;
        try { nxt = await getNextLesson(cur.id); } catch { nxt = null; }
        if (cancelled) return;
        setCurrentLesson(cur);
        setNextLesson(nxt);
      } catch (e) {
        if (!cancelled) setBootError(e?.message || 'Could not load lessons.');
      }
    })();
    return () => { cancelled = true; };
  }, [learner?.id, trackId]);

  // Two-option selector ONLY; if no next lesson exists, "next" is unselectable.
  const selected = selectedKey === 'next' && nextLesson ? nextLesson : currentLesson;

  // ── YouTube search: ≤10 min, title-anchored, exam-biased ──
  // Query: lesson title (primary anchor) + track label + "exam tutorial". The
  // "exam" term biases toward cert study content over general explainers. An
  // objective keyword is appended ONLY when the title is <4 words (too short to
  // be specific on its own). Unquoted — exact-phrase quotes narrowed results too
  // hard. Result titles/channels are filtered against ENTERTAINMENT_DENY before
  // duration checks. Per-lesson cache prevents a re-fetch on Current ↔ Next
  // toggle. Errors degrade to videoMissing — never block the tool.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;

    // Cache hit — skip both API calls.
    const cached = videoCacheRef.current.get(selected.id);
    if (cached) {
      setVideo(cached.video);
      setVideoMissing(cached.missing);
      setVideoLoading(false);
      return () => { cancelled = true; };
    }

    setVideo(null);
    setVideoMissing(false);
    setVideoLoading(true);

    const apiKey = import.meta.env.VITE_YOUTUBE_API_KEY;
    if (!apiKey) {
      videoCacheRef.current.set(selected.id, { video: null, missing: true });
      setVideoMissing(true); setVideoLoading(false);
      return () => { cancelled = true; };
    }

    const trackLabel       = (track?.label || trackId || 'IT').trim();
    // Title is the primary anchor; add an objective keyword only when the title
    // is too short (<4 words) to be specific on its own. "exam" biases YouTube
    // toward certification study content over general explainers.
    const titleWordCount   = selected.title.split(' ').length;
    const objectiveKeyword = titleWordCount < 4
      ? (selected.objectives?.[0]?.split(' ').slice(0, 4).join(' ') || '')
      : '';
    const query            = `${selected.title} ${objectiveKeyword} ${trackLabel} exam tutorial`.trim();

    (async () => {
      try {
        const searchUrl = `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=video&videoEmbeddable=true&relevanceLanguage=en&maxResults=15&key=${apiKey}`;
        const sr = await fetch(searchUrl).then(r => r.json());
        const snippetById = new Map();
        (sr?.items || []).forEach(it => {
          const vid     = it.id?.videoId;
          const title   = it.snippet?.title || '';
          const channel = it.snippet?.channelTitle || '';
          // Drop entertainment matches (Transformers cartoon, Disney trailers, etc.).
          if (!vid) return;
          if (isEntertainmentHit({ title, channel })) return;
          snippetById.set(vid, { title, channel });
        });
        const ids = [...snippetById.keys()];
        if (!ids.length) {
          if (cancelled) return;
          videoCacheRef.current.set(selected.id, { video: null, missing: true });
          setVideoMissing(true); setVideoLoading(false);
          return;
        }

        const detailsUrl = `https://www.googleapis.com/youtube/v3/videos?id=${ids.join(',')}&part=contentDetails&key=${apiKey}`;
        const dr = await fetch(detailsUrl).then(r => r.json());
        const detailsById = new Map((dr?.items || []).map(it => [it.id, it]));

        // Build the eligible list (preserving search-relevance order) so the
        // re-rank pass can score multiple candidates rather than just the top
        // duration-eligible hit.
        const eligible = ids
          .map(id => {
            const sec = parseIsoDuration(detailsById.get(id)?.contentDetails?.duration || '');
            if (!(sec > 0 && sec <= YT_DURATION_CAP_SEC)) return null;
            const snip = snippetById.get(id) || {};
            return { id, title: snip.title || '', channel: snip.channel || '', duration_seconds: sec };
          })
          .filter(Boolean);

        if (!eligible.length) {
          if (cancelled) return;
          videoCacheRef.current.set(selected.id, { video: null, missing: true });
          setVideoMissing(true); setVideoLoading(false);
          return;
        }

        // LLM re-rank: pick the most topical candidate. Falls back to the
        // search-relevance first hit on null / network error — the tool stays
        // useful even if /api/yt-rerank is down.
        let chosenId = eligible[0].id;
        if (eligible.length >= 2) {
          const winner = await rerankCandidates({
            lessonTitle:       selected.title,
            lessonObjectives:  selected.objectives || [],
            trackLabel:        trackLabel,
            candidates:        eligible,
          });
          if (cancelled) return;
          if (winner && eligible.some(e => e.id === winner)) chosenId = winner;
        }

        const chosen = eligible.find(e => e.id === chosenId) || eligible[0];
        const result = { id: chosen.id, title: chosen.title, duration: fmtDur(chosen.duration_seconds) };
        videoCacheRef.current.set(selected.id, { video: result, missing: false });
        setVideo(result);
        setVideoLoading(false);
        try { logYoutubeInteraction(learner.id, selected.id, selected.title, query); } catch { /* non-fatal */ }
      } catch {
        if (cancelled) return;
        videoCacheRef.current.set(selected.id, { video: null, missing: true });
        setVideoMissing(true); setVideoLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selected?.id, trackId, track?.label, learner?.id]);

  // ── Prove Understanding → CHECKPOINT_READY round-trip ────────────
  const proveUnderstanding = async () => {
    if (!selected || !learner?.id || !trackId) return;
    setQuizError(null);
    setQuizLoading(true);
    try {
      const statePayload = await buildStatePayload(learner.id, trackId, selected.id);
      const res = await callAI({
        trackId,
        conversationHistory: [],
        userInput: `Generate the end-of-lesson checkpoint quiz for: ${selected.title}.`,
        statePayload,
        lessonData: selected,
        learnerId: learner.id,
        sessionPhase:  'CHECKPOINT',
        turnDirective: 'CHECKPOINT_READY',
      });
      const parsed = parseResponse(res?.content || '');
      if (!parsed?.quiz?.questions?.length) {
        setQuizError('Could not generate a checkpoint right now. Please try again.');
        setQuizLoading(false);
        return;
      }
      setQuiz(parsed.quiz);
      setPhase('quiz');
    } catch (e) {
      setQuizError(e?.message || 'Could not generate the checkpoint.');
    } finally {
      setQuizLoading(false);
    }
  };

  // ── Mirror handleQuizComplete's persistence path (no engine instance) ──
  // Pass: markLessonComplete + advance current_lesson_id.
  // Fail: persist failed-checkpoint state on the selected lesson; the learner
  //       opens Study Console and the engine remediation flow takes over.
  const handleQuizDone = async (correct, total /* , answers */) => {
    const accuracy = total > 0 ? correct / total : 0;
    const passed   = accuracy >= PASS_THRESHOLD;
    if (!selected?.id) { setQuiz(null); return; }

    if (passed) {
      try {
        await markLessonComplete(
          learner.id, selected.id, trackId, selected.module_id, accuracy,
          { mastery_score: accuracy, mastery_level: accuracy >= 0.85 ? 'mastered' : 'understood' },
        );
        let advanceTo = selected.id;
        try {
          const nxt = await getNextLesson(selected.id);
          if (nxt?.id) advanceTo = nxt.id;
        } catch { /* end-of-track or transient — stay on this lesson */ }
        await upsertLearnerState(learner.id, trackId, {
          current_lesson_id:      advanceTo,
          last_checkpoint_result: 'pass',
          consecutive_failures:   0,
        });
      } catch (e) {
        console.error('VideoLessonTool pass persist:', e?.message);
      }
      setQuiz(null);
      setPhase('passed');
    } else {
      try {
        await upsertLessonProgress(learner.id, selected.id, trackId, selected.module_id, {
          mastery_score: accuracy,
          mastery_level: 'developing',
          status:        'in_progress',
        });
        await upsertLearnerState(learner.id, trackId, {
          current_lesson_id:      selected.id,
          last_checkpoint_result: 'fail',
        });
      } catch (e) {
        console.error('VideoLessonTool fail persist:', e?.message);
      }
      setQuiz(null);
      setPhase('failed');
    }
  };

  const goToStudyConsole = () => {
    if (typeof onSelectTrack !== 'function' || !trackId) return;
    onSelectTrack({ id: trackId, ...(track || {}) });
  };

  // ───────────────────────── render helpers ─────────────────────────
  const card = {
    background: 'var(--bg-secondary)', border: '1px solid var(--border-soft)',
    borderRadius: 12, padding: 18,
  };
  const heading = { fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' };
  const sub     = { fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 };

  // ── boot error / empty states ───────────────────────────────────
  if (bootError) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Video Lesson</h2>
          <p style={sub}>Watch a short lesson video, then prove understanding.</p>
        </div>
        <div style={{ ...card, textAlign: 'center', padding: 24 }}>
          <p style={{ ...sub, marginBottom: 0 }}>{bootError}</p>
        </div>
      </div>
    );
  }

  if (!currentLesson) {
    return (
      <div>
        <h2 style={heading}>Video Lesson</h2>
        <p style={{ ...sub, marginTop: 12 }}>Loading your lesson…</p>
      </div>
    );
  }

  // ── quiz running ────────────────────────────────────────────────
  if (phase === 'quiz' && quiz) {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Prove Understanding</h2>
          <p style={sub}>{selected.title}</p>
        </div>
        <QuizPanel quiz={quiz} onComplete={handleQuizDone} phase="CHECKPOINT" />
      </div>
    );
  }

  // ── passed ──────────────────────────────────────────────────────
  if (phase === 'passed') {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Lesson Complete</h2>
        </div>
        <div style={{ ...card, padding: 24 }}>
          <p style={{ fontSize: 14, color: 'var(--text-primary)', margin: '0 0 4px', fontWeight: 600 }}>
            ✓ {selected.title}
          </p>
          <p style={{ ...sub, marginBottom: 18 }}>
            Lesson marked complete. Return to Study Console to continue.
          </p>
          <button type="button" onClick={goToStudyConsole} style={btnPrimary}>
            Open Study Console →
          </button>
        </div>
      </div>
    );
  }

  // ── failed → hand off to Study Console for normal remediation ──
  if (phase === 'failed') {
    return (
      <div>
        <div style={{ marginBottom: 18 }}>
          <h2 style={heading}>Gap Detected</h2>
        </div>
        <div style={{ ...card, padding: 24 }}>
          <p style={{ ...sub, marginBottom: 18 }}>
            Open Study Console to repair this together — AdaptOS will re-teach the weak
            concept and re-check before you advance.
          </p>
          <button type="button" onClick={goToStudyConsole} style={btnPrimary}>
            Open Study Console →
          </button>
        </div>
      </div>
    );
  }

  // ── default: selector + video preview + Prove Understanding CTA ──
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={heading}>Video Lesson</h2>
        <p style={sub}>Watch a short lesson video, then prove understanding.</p>
      </div>

      {/* Selector — max 2 options, enforced */}
      <div id="hint-vl-selector" style={{ display: 'grid', gridTemplateColumns: nextLesson ? '1fr 1fr' : '1fr', gap: 10, marginBottom: 18 }}>
        <LessonOption
          label="Current Lesson"
          lesson={currentLesson}
          active={selectedKey === 'current'}
          onClick={() => setSelectedKey('current')}
        />
        {nextLesson && (
          <LessonOption
            label="Next Lesson"
            lesson={nextLesson}
            active={selectedKey === 'next'}
            onClick={() => setSelectedKey('next')}
          />
        )}
      </div>

      {/* Video preview */}
      <div id="hint-vl-video" style={{ ...card, marginBottom: 18 }}>
        {videoLoading && (
          <p style={{ ...sub, margin: 0 }}>Finding a short video for "{selected?.title}"…</p>
        )}
        {!videoLoading && video?.id && (
          <>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', borderRadius: 10, background: '#000' }}>
              <iframe
                key={video.id}
                src={`https://www.youtube.com/embed/${video.id}?rel=0&modestbranding=1`}
                title={video.title || 'Lesson video'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            </div>
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
              <p style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0, fontWeight: 600, lineHeight: 1.4 }}>
                {video.title}
              </p>
              {video.duration && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, flexShrink: 0 }}>
                  {video.duration}
                </span>
              )}
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '6px 0 0', fontStyle: 'italic' }}>
              Video preview — not a completion step.
            </p>
          </>
        )}
        {!videoLoading && videoMissing && (
          <p style={{ ...sub, margin: 0 }}>
            No short video found for this lesson yet. Continue in Study Console.
          </p>
        )}
      </div>

      {/* Prove Understanding — always visible, always actionable */}
      <button
        type="button"
        id="hint-vl-prove"
        onClick={proveUnderstanding}
        disabled={quizLoading}
        style={{ ...btnPrimary, width: '100%', opacity: quizLoading ? 0.7 : 1, cursor: quizLoading ? 'default' : 'pointer' }}
      >
        {quizLoading ? 'Preparing checkpoint…' : 'Prove Understanding'}
      </button>
      {quizError && (
        <p style={{ fontSize: 12, color: 'var(--progress-weak)', margin: '10px 0 0', textAlign: 'center' }}>
          {quizError}
        </p>
      )}
    </div>
  );
}

// ── presentational sub-component ──────────────────────────────────
function LessonOption({ label, lesson, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        textAlign: 'left',
        padding: '14px 16px',
        borderRadius: 12,
        border: `1.5px solid ${active ? 'var(--accent-primary)' : 'var(--border-soft)'}`,
        background: active ? 'var(--accent-soft)' : 'var(--bg-secondary)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all 0.15s',
      }}
    >
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase',
        color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
      }}>
        {label}
      </span>
      <p style={{
        fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
        margin: '4px 0 0', lineHeight: 1.35,
      }}>
        {lesson.title}
      </p>
    </button>
  );
}

const btnPrimary = {
  padding: '12px 22px',
  borderRadius: 10,
  background: 'var(--accent-primary)',
  border: 'none',
  color: 'var(--accent-text)',
  fontSize: 14,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  letterSpacing: 0.2,
};
